import React from 'react'
import { ActivityIndicator } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { publishEvent } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import { AgentDetails } from '@zet/customer'
import { Button, Stack, colors } from '@zet/design-system'
import { i18n } from '@zet/localization'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useGetNextStateForProductFlow } from '../../../../api/brands/hooks'
import { BottomsheetTypes } from '../../../../api/brands/types'
import { useProductFlowApply } from '../../../../hooks/useProductFlowApply'
import { Header } from '../header'
import { FormFields } from './formFields'

const validationSchema = z.object({
  panNumber: z
    .string()
    .min(1, { message: 'Field is required!' })
    .regex(
      new RegExp(/(^([a-zA-Z]{5})([0-9]{4})([a-zA-Z]{1})$)/),
      'Invalid Pan Number',
    )
    .max(10, 'Invalid Pan Number'),
  dob: z.date(),
})

type ValidationSchema = z.infer<typeof validationSchema>

const BasicDetails = ({
  agentData,
  handleBottomsheetState,
}: {
  agentData: AgentDetails
  handleBottomsheetState: (state: BottomsheetTypes) => void
}) => {
  const toast = useToast()

  const {
    control,
    formState: { errors, isValid },
    setError,
    handleSubmit,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const { mutate, isLoading } = useGetNextStateForProductFlow()

  const { apply, shareLinkLoading } = useProductFlowApply(
    agentData,
    handleBottomsheetState,
  )
  function closeBottomSheet() {
    handleBottomsheetState(null)
  }

  function onSubmit(values: { panNumber: string; dob: string }) {
    publishEvent('click_action', {
      item_name: 'shortflow_extra_personal_details',
    })
    const dob = moment(values?.dob).format('YYYY-MM-DD')
    mutate(
      {
        customerPayload: {
          panNo: values?.panNumber,
          categoryId: agentData?.categoryId,
          dob: dob,
          customerId: sessionStorage.getItem('customerId'),
        },
        userState: 'SHOWN_PAN_COLLECTION_SCREEN_V3',
      },
      {
        onSuccess(data) {
          if (data?.code === 400) {
            setError('panNumber', {
              message: data?.message,
            })
          }
          if (data?.action === 'SHOW_SECONDARY_PHONE_NUMBER_SCREEN_V3') {
            handleBottomsheetState('SECONDARY_PHONE_NUMBERS', data)
          }
          if (data?.action === 'SHOW_RECOMMENDATION_SCREEN') {
            apply()
          }
        },
        onError(err) {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            description:
              err?.response?.data?.error?.message ||
              i18n.t('toastMessages.somethingWentWrong'),
            hideAfter: 3000,
          })
        },
      },
    )
  }

  return (
    <>
      <Stack>
        <Header
          title={i18n.t('addCusProductjourney.enterBasicDetails')}
          closeBottomSheet={closeBottomSheet}
        />
        {isLoading || shareLinkLoading ? (
          <Stack className="pt-6">
            <ActivityIndicator
              size={'large'}
              color={colors.primary[100]}
              className="items-center justify-center"
            />
          </Stack>
        ) : (
          <FormFields control={control} errors={errors} />
        )}
      </Stack>
      <Stack className="bg-neutral-white100 absolute bottom-0 w-full px-3 py-2">
        <Button
          className="bg-tertiary-200 w-full"
          disabled={!isValid}
          size={'lg'}
          loading={isLoading || shareLinkLoading}
          onPress={handleSubmit(onSubmit)}
        >
          {i18n.t('bottomView.continue')}
        </Button>
      </Stack>
    </>
  )
}

export { BasicDetails }
